import { useEffect, useState } from "react";
import { Check, ExternalLink, FileText, Loader2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../../lib/supabase";
import { StatusBadge } from "../register/UnderReview";

export function useSignedUrl(bucket, path) {
  const [state, setState] = useState({ url: null, failed: false });
  useEffect(() => {
    if (!path) { setState({ url: null, failed: false }); return; }
    let alive = true;
    let timer;
    const sign = async () => {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (!alive) return;
      if (error || !data?.signedUrl) setState({ url: null, failed: true });
      else {
        setState({ url: data.signedUrl, failed: false });
        timer = setTimeout(sign, 55 * 60 * 1000);
      }
    };
    sign();
    return () => { alive = false; clearTimeout(timer); };
  }, [bucket, path]);
  return state;
}

export function SignedThumb({ path, name }) {
  const { url, failed } = useSignedUrl("profile-photos", path);
  if (!path || failed) {
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#F4EFEA] text-[#7A0016]">
        <UserRound className="h-4 w-4" />
      </span>
    );
  }
  return url ? (
    <img src={url} alt={name} data-testid="profile-thumb" className="h-10 w-10 rounded-full border border-[#D4AF37]/50 object-cover" />
  ) : (
    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#F4EFEA]"><Loader2 className="h-3.5 w-3.5 animate-spin text-[#7A0016]" /></span>
  );
}

// item: row of profile_photos (kind="photo") or verification_documents (kind="doc")
export function MediaCard({ item, kind, canReview, onChanged, caption }) {
  const bucket = kind === "photo" ? "profile-photos" : "id-documents";
  const { url, failed } = useSignedUrl(bucket, item.storage_path);
  const [busy, setBusy] = useState("");
  const isPdf = item.storage_path.toLowerCase().endsWith(".pdf");
  const review = async (status) => {
    setBusy(status);
    const { error } = await supabase.from(kind === "photo" ? "profile_photos" : "verification_documents").update({ status }).eq("id", item.id);
    setBusy("");
    if (error) return toast.error(error.message);
    toast.success(kind === "doc" ? (status === "approved" ? "ID marked verified" : "ID rejected") : `Photo ${status}`);
    onChanged?.();
  };
  const approveLabel = kind === "doc" ? "Verify" : "Approve";
  return (
    <div data-testid={`media-card-${item.id}`} className="overflow-hidden rounded-xl border border-[#D4AF37]/35 bg-white">
      <div className="relative flex aspect-square items-center justify-center bg-[#F4EFEA]">
        {!url ? (failed ? <UserRound data-testid={`media-unavailable-${item.id}`} className="h-10 w-10 text-[#D4AF37]" /> : <Loader2 className="h-5 w-5 animate-spin text-[#7A0016]" />) : isPdf ? (
          <FileText className="h-10 w-10 text-[#7A0016]" />
        ) : (
          <img src={url} alt="" className="h-full w-full object-cover" />
        )}
        {url && (
          <a href={url} target="_blank" rel="noreferrer" data-testid={`media-open-${item.id}`} className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-[#7A0016] shadow" aria-label="Open file">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {item.is_primary && <span className="absolute left-2 top-2 rounded-full bg-[#7A0016] px-2 py-0.5 text-[10px] font-semibold text-[#F5E6BA]">Profile photo</span>}
      </div>
      <div className="space-y-2 p-3">
        {caption}
        <div className="flex items-center justify-between gap-2">
          <StatusBadge status={kind === "doc" && item.status === "approved" ? "verified" : item.status} />
          {canReview && (
            <div className="flex gap-1">
              <button type="button" disabled={!!busy || item.status === "approved"} onClick={() => review("approved")} data-testid={`media-approve-${item.id}`} title={approveLabel} className="rounded-full bg-emerald-600 p-1.5 text-white disabled:opacity-40">
                {busy === "approved" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button type="button" disabled={!!busy || item.status === "rejected"} onClick={() => review("rejected")} data-testid={`media-reject-${item.id}`} title="Reject" className="rounded-full bg-[#A31828] p-1.5 text-white disabled:opacity-40">
                {busy === "rejected" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
